import { MageXPatient } from "@prisma/client";
import { JsonArray, JsonObject } from "@prisma/client/runtime/library";
import ValueDisplayScore from "./ValueDisplayScore";
import ValueDisplayEnumArray from "./ValueDisplayEnumArray";
import ValueDisplayNotArray from "./ValueDisplayNotArray";
import ProblemListDisplay from "./ProblemListDisplay";
import { isProblemList } from "@app/utils/problem-list";
import { PatientSchema, TypeEnum } from "@api/src/types/few-schot-schema";
import { useState, useEffect, memo } from "react";

interface ValueDisplayProps {
  property: keyof MageXPatient;
  type?: TypeEnum;
  debug?: boolean;
  aggregatedValue:
    | string
    | number
    | boolean
    | JsonArray
    | Date
    | JsonObject
    | null
    | undefined;
  correctedValue:
    | string
    | number
    | boolean
    | JsonArray
    | Date
    | JsonObject
    | null
    | undefined;
  onlyTruth?: boolean;
  schema: PatientSchema;
  label: string;
  // Temporal projection page: render values as bordered "petit encart" cards
  // (see ValueCard) — one card per array item — instead of right-aligned inline
  // text. Off everywhere else, so no other page is affected.
  cardVariant?: boolean;
  // True when the user has explicitly recorded a truth for this field — even
  // if that truth is empty (e.g. "Confirmer l'absence de valeur"). Leaf
  // components must then trust ``correctedValue`` instead of falling back to
  // ``aggregatedValue`` when the truth is empty, so a validated-empty value
  // renders as "Non déterminé" with the green check rather than re-showing
  // the stale aggregated answer.
  hasValidatedTruth?: boolean;
  /** Le geste gouvernant est une correction — seule condition qui met une série de
   *  scores en vert. */
  isCorrected?: boolean;
}

type Score = {
  score: number;
  date: string;
};

const ValueDisplay = memo(function ValueDisplay({
  property,
  label,
  type = "string",
  debug = false,
  correctedValue,
  aggregatedValue,
  onlyTruth = false,
  schema,
  hasValidatedTruth = false,
  isCorrected = false,
  cardVariant = false,
}: ValueDisplayProps) {
  const [_correctedValue, _setCorrectedValue] = useState(() => correctedValue);

  // Keep internal copy in sync when the parent updates correctedValue
  // (e.g. after saving a boolean true/false via the edit modal).
  useEffect(() => {
    _setCorrectedValue(correctedValue);
  }, [correctedValue]);

  const valueIsEnum = type === "array_enum" || type === "array_string";
  const valueIsScore = type === "scores";
  // A synthesised clinical field (services.field_synthesis) arrives as a two-tier problem list
  // instead of a flat array — detected by shape so it renders the same on any array_* field that
  // opted in, and falls back to the flat renderer everywhere else (e.g. the non-temporal page).
  const problemValue = _correctedValue ?? aggregatedValue;
  const valueIsProblemList = isProblemList(problemValue);

  return (
    <>
      {valueIsProblemList ? (
        <ProblemListDisplay
          problems={problemValue}
          labels={schema[property]?.value_labels}
        />
      ) : valueIsEnum ? (
        <ValueDisplayEnumArray
          correctedValue={_correctedValue as string[]}
          aggregatedValue={aggregatedValue as string[]}
          onlyTruth={onlyTruth}
          schema={schema}
          property={property}
          type={type}
          label={label}
          debug={debug}
          setCorrectedValue={_setCorrectedValue}
          hasValidatedTruth={hasValidatedTruth}
          cardVariant={cardVariant}
        />
      ) : valueIsScore ? (
        <ValueDisplayScore
          property={property}
          aggregatedValue={aggregatedValue as Array<Score>}
          onlyTruth={onlyTruth}
          hasValidatedTruth={hasValidatedTruth}
          isCorrected={isCorrected}
          correctedValue={_correctedValue as Array<Score> | null | undefined}
        />
      ) : (
        <ValueDisplayNotArray
          property={property}
          type={type}
          label={label}
          debug={debug}
          setCorrectedValue={_setCorrectedValue}
          aggregatedValue={aggregatedValue}
          correctedValue={_correctedValue}
          onlyTruth={onlyTruth}
          schema={schema}
          hasValidatedTruth={hasValidatedTruth}
          cardVariant={cardVariant}
        />
      )}
    </>
  );
});

export default ValueDisplay;
